import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiX,
  FiSend,
  FiCompass,
  FiLifeBuoy,
  FiMessageSquare,
  FiPlus,
  FiAlertCircle,
  FiCheckCircle,
  FiRefreshCw,
  FiExternalLink,
} from "react-icons/fi";
import {
  sendChatMessage,
  fetchChatHistory,
  createNewSession,
  createSupportTicket,
  fetchUserTickets,
} from "../../services/aiChatService";
import "./AIConcierge.css";

const QUICK_PROMPTS = [
  "🏛️ Sigiriya & Cultural Triangle Guide",
  "🏨 Scenic Stays in Kandy",
  "🍃 Ella Hiking & Nine Arch Bridge",
  "🌊 Mirissa Surf & Whale Watching",
  "🚆 Kandy to Ella Train Tickets & Tips",
  "🛡️ I need human support assistance",
];

export default function AIConciergeDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("chat"); // 'chat' | 'tickets'
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  
  // Support Tickets tab state
  const [tickets, setTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDesc, setTicketDesc] = useState("");
  const [ticketPriority, setTicketPriority] = useState("medium");
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [ticketSuccessMsg, setTicketSuccessMsg] = useState("");

  const chatEndRef = useRef(null);

  // Initialize session from localStorage or create new
  useEffect(() => {
    const savedToken = localStorage.getItem("soloway_ai_session_token");
    const savedId = localStorage.getItem("soloway_ai_session_id");

    if (savedId && savedToken) {
      setSessionId(savedId);
      setSessionToken(savedToken);
      loadHistory(savedId);
    } else {
      initNewSession();
    }
  }, []);

  // Fetch tickets when user opens tickets tab
  useEffect(() => {
    if (activeTab === "tickets" && isOpen) {
      loadTickets();
    }
  }, [activeTab, isOpen]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (activeTab === "chat") {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading, activeTab]);

  const initNewSession = async () => {
    try {
      const data = await createNewSession();
      if (data.sessionId && data.sessionToken) {
        setSessionId(data.sessionId);
        setSessionToken(data.sessionToken);
        localStorage.setItem("soloway_ai_session_id", data.sessionId);
        localStorage.setItem("soloway_ai_session_token", data.sessionToken);
        setMessages([]);
      }
    } catch (err) {
      console.warn("Could not create fresh session token:", err);
    }
  };

  const loadHistory = async (sId) => {
    try {
      const res = await fetchChatHistory(sId);
      if (res.success && Array.isArray(res.messages)) {
        setMessages(res.messages);
      }
    } catch (err) {
      console.warn("Could not load chat history:", err);
    }
  };

  const loadTickets = async () => {
    setIsLoadingTickets(true);
    try {
      const res = await fetchUserTickets();
      if (res.success && Array.isArray(res.tickets)) {
        setTickets(res.tickets);
      }
    } catch (err) {
      console.warn("Could not load user tickets:", err);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  const handleSendMessage = async (textToSend) => {
    const text = (textToSend || inputVal).trim();
    if (!text || isLoading) return;

    setInputVal("");
    const userMsg = {
      id: `temp-${Date.now()}`,
      sender: "user",
      content: text,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await sendChatMessage({
        message: text,
        sessionId,
        sessionToken,
      });

      if (res.success && res.message) {
        setMessages((prev) => [...prev, res.message]);
        if (res.sessionId && !sessionId) {
          setSessionId(res.sessionId);
          setSessionToken(res.sessionToken);
          localStorage.setItem("soloway_ai_session_id", res.sessionId);
          localStorage.setItem("soloway_ai_session_token", res.sessionToken);
        }
      }
    } catch (err) {
      const errorMsg = {
        id: `err-${Date.now()}`,
        sender: "assistant",
        content: "⚠️ I encountered an issue connecting to the concierge service. Please check your internet connection and try again.",
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTicketSubmit = async (e) => {
    e.preventDefault();
    if (!ticketSubject.trim() || !ticketDesc.trim()) return;

    setIsSubmittingTicket(true);
    try {
      const res = await createSupportTicket({
        subject: ticketSubject,
        description: ticketDesc,
        priority: ticketPriority,
        sessionId,
      });

      if (res.success) {
        setTicketSuccessMsg(`Ticket #${res.ticket.id} created successfully!`);
        setTicketSubject("");
        setTicketDesc("");
        setTicketPriority("medium");
        setTimeout(() => {
          setShowNewTicketModal(false);
          setTicketSuccessMsg("");
          loadTickets();
        }, 1500);
      }
    } catch (err) {
      console.error("Ticket submission failed:", err);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  // Simple clean markdown parser for assistant responses
  const renderFormattedContent = (content) => {
    if (!content) return null;
    const lines = content.split("\n");

    return lines.map((line, idx) => {
      // H3 heading
      if (line.startsWith("### ")) {
        return <h3 key={idx}>{renderInlineMarkdown(line.replace("### ", ""))}</h3>;
      }
      // H4 heading
      if (line.startsWith("#### ")) {
        return <h4 key={idx} style={{ color: "#38bdf8", margin: "6px 0 4px 0" }}>{renderInlineMarkdown(line.replace("#### ", ""))}</h4>;
      }
      // Bullet items
      if (line.startsWith("* ") || line.startsWith("- ")) {
        return (
          <li key={idx} style={{ marginLeft: "14px", marginBottom: "4px" }}>
            {renderInlineMarkdown(line.slice(2))}
          </li>
        );
      }
      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        const itemText = line.replace(/^\d+\.\s/, "");
        return (
          <li key={idx} style={{ marginLeft: "14px", marginBottom: "4px", listStyleType: "decimal" }}>
            {renderInlineMarkdown(itemText)}
          </li>
        );
      }
      // Empty line
      if (!line.trim()) {
        return <div key={idx} style={{ height: "6px" }} />;
      }
      // Regular paragraph
      return <p key={idx}>{renderInlineMarkdown(line)}</p>;
    });
  };

  // Helper for **bold** and *italic*
  const renderInlineMarkdown = (text) => {
    if (!text) return "";
    // Split by bold (**bold**)
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  const handleCardClick = (card) => {
    if (!card) return;
    onClose();

    if (card.type === "hotel") {
      const hotelState = card.payload || {
        id: card.id,
        hotelName: card.title,
        name: card.title,
        address: card.subtitle,
        image: card.image,
      };
      navigate(card.id ? `/travelerhotel/${card.id}` : "/travelerhotel", {
        state: { hotel: hotelState },
      });
    } else if (card.type === "guide") {
      const guideState = card.payload || {
        id: card.id,
        name: card.title,
        guideName: card.subtitle,
        price: card.price,
      };
      navigate(card.id ? `/guideprofilebook/${card.id}` : "/guideprofilebook", {
        state: { guide: guideState },
      });
    } else if (card.actionUrl) {
      navigate(card.actionUrl);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`sw-ai-backdrop ${isOpen ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Body */}
      <aside className={`sw-ai-drawer ${isOpen ? "open" : ""}`} aria-label="SoloWay Travel Concierge">
        {/* Header */}
        <header className="sw-ai-header">
          <div className="sw-ai-brand">
            <div className="sw-ai-avatar-circle">
              <FiCompass />
            </div>
            <div className="sw-ai-title-wrap">
              <h3>SoloWay Concierge</h3>
              <span className="sw-ai-status-pill">
                <span className="status-dot" /> AI Assistant Online
              </span>
            </div>
          </div>

          <div className="sw-ai-header-actions">
            <button
              className="sw-ai-icon-btn"
              title="Reset Chat Session"
              onClick={initNewSession}
            >
              <FiRefreshCw />
            </button>
            <button
              className="sw-ai-icon-btn"
              title="Close Drawer"
              onClick={onClose}
            >
              <FiX />
            </button>
          </div>
        </header>

        {/* Tab Switcher */}
        <nav className="sw-ai-tabs">
          <button
            className={`sw-ai-tab-btn ${activeTab === "chat" ? "active" : ""}`}
            onClick={() => setActiveTab("chat")}
          >
            <FiMessageSquare />
            <span>AI Concierge</span>
          </button>
          <button
            className={`sw-ai-tab-btn ${activeTab === "tickets" ? "active" : ""}`}
            onClick={() => setActiveTab("tickets")}
          >
            <FiLifeBuoy />
            <span>My Support Tickets</span>
            {tickets.length > 0 && (
              <span className="sw-tab-badge">{tickets.length}</span>
            )}
          </button>
        </nav>

        {/* TAB 1: AI Concierge Chat */}
        {activeTab === "chat" && (
          <>
            <div className="sw-ai-chat-body">
              {/* Welcome Card & Chips if conversation is empty */}
              {messages.length === 0 && (
                <div className="sw-ai-welcome-card">
                  <h4>Ayubowan! 🇱🇰 How can I assist your trip?</h4>
                  <p>
                    I provide real-time recommendations for Sri Lankan hotels, certified tour guides, cultural tips, and scenic itineraries.
                  </p>
                  <div className="sw-ai-chips-wrap">
                    {QUICK_PROMPTS.map((chip, index) => (
                      <button
                        key={index}
                        className="sw-ai-chip"
                        onClick={() => handleSendMessage(chip)}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message List */}
              {messages.map((msg) => {
                const isUser = msg.sender === "user";
                const cards = msg.metadata?.cards || [];
                const escalatedTicket = msg.metadata?.escalatedTicket;

                return (
                  <div
                    key={msg.id || Math.random()}
                    className={`sw-msg-row ${isUser ? "user" : "assistant"}`}
                  >
                    <div className="sw-msg-avatar">
                      {isUser ? "👤" : "🇱🇰"}
                    </div>
                    <div className="sw-msg-bubble">
                      {isUser ? (
                        <p>{msg.content}</p>
                      ) : (
                        <div>
                          {renderFormattedContent(msg.content)}

                          {/* Escalation Alert Box */}
                          {escalatedTicket && (
                            <div className="sw-escalate-card">
                              <FiAlertCircle className="sw-esc-icon" />
                              <div className="sw-esc-body">
                                <h5>Support Ticket Escalated: #{escalatedTicket.id}</h5>
                                <p>
                                  Subject: <strong>{escalatedTicket.subject}</strong> | Status:{" "}
                                  <span style={{ color: "#34d399", textTransform: "capitalize" }}>
                                    {escalatedTicket.status}
                                  </span>
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Recommendation Cards */}
                          {cards.length > 0 && (
                            <div className="sw-card-deck">
                              {cards.map((card, cIdx) => (
                                <div
                                  key={cIdx}
                                  className="sw-recommend-card"
                                  onClick={() => handleCardClick(card)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="sw-rec-info">
                                    <span className="sw-rec-badge">{card.badge}</span>
                                    <div className="sw-rec-title">{card.title}</div>
                                    <div className="sw-rec-sub">{card.subtitle}</div>
                                    {card.price && <div className="sw-rec-price">{card.price}</div>}
                                  </div>
                                  <button
                                    className="sw-rec-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCardClick(card);
                                    }}
                                  >
                                    {card.actionLabel} <FiExternalLink style={{ marginLeft: "4px" }} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <span className="sw-msg-time">
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : ""}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Typing indicator */}
              {isLoading && (
                <div className="sw-msg-row assistant">
                  <div className="sw-msg-avatar">🇱🇰</div>
                  <div className="sw-typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Fallback Support Bar */}
            <div className="sw-ai-fallback-bar">
              <span>Can't find your answer?</span>
              <button
                className="sw-ai-fallback-link"
                onClick={() => {
                  setShowNewTicketModal(true);
                  setTicketSubject("Inquiry via AI Concierge");
                }}
              >
                Open a Support Ticket →
              </button>
            </div>

            {/* Message Input */}
            <form
              className="sw-ai-input-wrap"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <input
                type="text"
                className="sw-ai-input"
                placeholder="Ask anything about traveling in Sri Lanka..."
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="submit"
                className="sw-ai-send-btn"
                disabled={!inputVal.trim() || isLoading}
                title="Send Message"
              >
                <FiSend />
              </button>
            </form>
          </>
        )}

        {/* TAB 2: Support Tickets */}
        {activeTab === "tickets" && (
          <div className="sw-tickets-container">
            <div className="sw-tickets-header">
              <h4>My Support Inquiries</h4>
              <button
                className="sw-new-ticket-btn"
                onClick={() => setShowNewTicketModal(true)}
              >
                <FiPlus /> New Ticket
              </button>
            </div>

            {isLoadingTickets ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                Loading tickets...
              </div>
            ) : tickets.length === 0 ? (
              <div className="sw-empty-tickets">
                <FiLifeBuoy />
                <p>No active support tickets found.</p>
                <p style={{ fontSize: "12px" }}>
                  Need help? Create a ticket and our 24/7 team will resolve it.
                </p>
              </div>
            ) : (
              tickets.map((t) => (
                <div key={t.id} className="sw-ticket-card">
                  <div className="sw-tcard-top">
                    <span className="sw-tid">#TICK-{t.id}</span>
                    <span className={`sw-tstatus ${t.status}`}>{t.status.replace("_", " ")}</span>
                  </div>
                  <h5 className="sw-tsubject">{t.subject}</h5>
                  <p className="sw-tdesc">{t.description}</p>
                  
                  {t.resolution_notes && (
                    <div className="sw-tnotes">
                      <strong>Support Agent Resolution:</strong>
                      <p style={{ margin: "4px 0 0 0" }}>{t.resolution_notes}</p>
                    </div>
                  )}

                  <div className="sw-tmeta">
                    <span className={`sw-tpriority ${t.priority}`}>Priority: {t.priority}</span>
                    <span>{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </aside>

      {/* Ticket Create Modal */}
      {showNewTicketModal && (
        <div className="sw-modal-overlay" onClick={() => setShowNewTicketModal(false)}>
          <div className="sw-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Open Support Ticket</h3>

            {ticketSuccessMsg ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: "#34d399" }}>
                <FiCheckCircle style={{ fontSize: "36px", marginBottom: "8px" }} />
                <p>{ticketSuccessMsg}</p>
              </div>
            ) : (
              <form onSubmit={handleCreateTicketSubmit}>
                <div className="sw-form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    className="sw-form-input"
                    placeholder="Brief summary of your issue or question"
                    value={ticketSubject}
                    onChange={(e) => setTicketSubject(e.target.value)}
                    required
                  />
                </div>

                <div className="sw-form-group">
                  <label>Priority</label>
                  <select
                    className="sw-form-select"
                    value={ticketPriority}
                    onChange={(e) => setTicketPriority(e.target.value)}
                  >
                    <option value="low">Low - General Inquiry</option>
                    <option value="medium">Medium - Booking or Itinerary Question</option>
                    <option value="high">High - Payment / Safety / Urgent Issue</option>
                  </select>
                </div>

                <div className="sw-form-group">
                  <label>Detailed Description</label>
                  <textarea
                    className="sw-form-textarea"
                    rows={4}
                    placeholder="Describe your inquiry in detail..."
                    value={ticketDesc}
                    onChange={(e) => setTicketDesc(e.target.value)}
                    required
                  />
                </div>

                <div className="sw-modal-actions">
                  <button
                    type="button"
                    className="sw-modal-cancel"
                    onClick={() => setShowNewTicketModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="sw-modal-submit"
                    disabled={isSubmittingTicket}
                  >
                    {isSubmittingTicket ? "Submitting..." : "Submit Ticket"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
